import "./JobsBoardSettingsDrawer.css";
import { MouseEvent, useState } from "react";
import { useLocation } from "react-router-dom";
import { Drawer } from "../../../../components/drawers/Drawer";
import Icon from "../../../../components/base/Icon";
import ToggleSwitch from "../../../../components/form/ToggleSwitch";
import { BOARD_VIEW_MODES } from "../utils/jobsBoardState";
import { BoardDensity, columnPartOn, defaultBoardSettings, GRID_FIELDS, gridFieldOn, LIST_COLUMNS, LIST_ID_COLUMN, ListColumnDef, ListColumnSetting, RowTint, saveBoardSettings, useBoardSettings } from "../utils/jobsBoardSettings";
import { withAlpha } from "../../../../core/Helper";


// The Row colors choices, each drawn as a tiny painted row.
const ROW_TINT_OPTIONS: { id: RowTint, title: string, description: string }[] = [
    { id: "soft", title: "Soft", description: "A faint touch of the colour" },
    { id: "fade", title: "Fade", description: "Fades out by the middle" },
    { id: "full", title: "Full", description: "The whole row in the colour" },
]
const DENSITY_OPTIONS: { id: BoardDensity, title: string, description: string, gap: number }[] = [
    { id: "compact", title: "Compact", description: "More jobs on screen; one line per list row", gap: 3 },
    { id: "default", title: "Default", description: "The board as it is", gap: 6 },
    { id: "comfortable", title: "Comfortable", description: "More room around each job", gap: 10 },
]
const PREVIEW_COLOR = "#7239EA"
const previewBackground = (t: RowTint) =>
    t == "soft" ? `linear-gradient(90deg, ${withAlpha(PREVIEW_COLOR, .11)} 0%, transparent 35%)`
        : t == "fade" ? `linear-gradient(90deg, ${withAlpha(PREVIEW_COLOR, .18)} 0%, transparent 55%)`
            : withAlpha(PREVIEW_COLOR, .18)


interface IProps {
    onClose: () => void
}

// Customize Board: each person shapes their own jobs board. Every view has its own settings and
// the drawer shows those of the view the person is in; changes apply (and are saved) at once.
export default function JobsBoardSettingsDrawer(props: IProps) {

    const location = useLocation()
    const settings = useBoardSettings()

    const view = BOARD_VIEW_MODES.find(m => m.link.toLowerCase() == location.pathname.toLowerCase())

    // Columns being dragged: the dragged column's index and the slot it would drop into.
    const [dragFrom, dragFromSet] = useState<number>()
    const [dragOver, dragOverSet] = useState<number>()
    const [expanded, expandedSet] = useState<string[]>([])

    const columns = settings.list.columns

    const saveColumns = (next: ListColumnSetting[]) =>
        saveBoardSettings({ ...settings, list: { ...settings.list, columns: next } })

    const toggleColumn = (i: number, visible: boolean) =>
        saveColumns(columns.map((c, k) => k == i ? { ...c, visible } : c))

    const moveColumn = (from: number, to: number) => {
        if (from == to) return
        const next = [...columns]
        const [moved] = next.splice(from, 1)
        next.splice(to > from ? to - 1 : to, 0, moved)
        saveColumns(next)
    }

    const shownCount = columns.filter(c => c.visible).length

    // Columns whose piece list is open.
    const hasParts = (def: ListColumnDef) => (def.parts ?? []).some(p => !p.locked)
    const togglePart = (i: number, part: string, on: boolean) =>
        saveColumns(columns.map((c, k) => k == i ? { ...c, parts: { ...c.parts, [part]: on } } : c))

    // Row colours: one choice shared by every view, shown in each view's settings.
    const rowTintSettings = (
        <div className="board-settings-section">
            <div className="board-settings-section-head">
                <div>
                    <div className="board-settings-section-title">
                        <Icon icon="fill-drip" type="solid" size={13} className="me-2" />
                        Row Colors
                    </div>
                    <div className="board-settings-section-desc">
                        How a job's colour tints its row or card. One choice for every view.
                    </div>
                </div>
            </div>
            <div className="board-settings-tints">
                {
                    ROW_TINT_OPTIONS.map(o => {
                        const on = settings.rowTint == o.id
                        return (
                            <a key={`row-tint-${o.id}`} href="#" role="radio" aria-checked={on}
                                className={`board-settings-tint${on ? " active" : ""}`}
                                onClick={e => {
                                    e.preventDefault()
                                    saveBoardSettings({ ...settings, rowTint: o.id })
                                }}>
                                <span className="board-settings-tint-preview" style={{ background: previewBackground(o.id) }}>
                                    <span className="board-settings-tint-band" style={{ backgroundColor: PREVIEW_COLOR }} />
                                    <span className="board-settings-tint-line" />
                                    <span className="board-settings-tint-line is-short" />
                                </span>
                                <span className="board-settings-tint-title">
                                    {o.title}
                                    {on && <Icon icon="circle-check" type="solid" size={12} className="ms-auto text-primary" />}
                                </span>
                                <span className="board-settings-tint-desc">{o.description}</span>
                            </a>
                        )
                    })
                }
            </div>
        </div>
    )

    // Density: one choice shared by every view, like the row colours.
    const densitySettings = (
        <div className="board-settings-section">
            <div className="board-settings-section-head">
                <div>
                    <div className="board-settings-section-title">
                        <Icon icon="line-height" type="solid" size={13} className="me-2" />
                        Density
                    </div>
                    <div className="board-settings-section-desc">
                        How tightly jobs are packed on the board. One choice for every view.
                    </div>
                </div>
            </div>
            <div className="board-settings-tints">
                {
                    DENSITY_OPTIONS.map(o => {
                        const on = settings.density == o.id
                        return (
                            <a key={`density-${o.id}`} href="#" role="radio" aria-checked={on}
                                className={`board-settings-tint${on ? " active" : ""}`}
                                onClick={e => {
                                    e.preventDefault()
                                    saveBoardSettings({ ...settings, density: o.id })
                                }}>
                                <span className="board-settings-tint-preview board-settings-density-preview" style={{ gap: o.gap }}>
                                    {
                                        [0, 1, 2, 3].slice(0, o.id == "compact" ? 4 : o.id == "default" ? 3 : 2).map(k => (
                                            <span key={k} className="board-settings-tint-line" />
                                        ))
                                    }
                                </span>
                                <span className="board-settings-tint-title">
                                    {o.title}
                                    {on && <Icon icon="circle-check" type="solid" size={12} className="ms-auto text-primary" />}
                                </span>
                                <span className="board-settings-tint-desc">{o.description}</span>
                            </a>
                        )
                    })
                }
            </div>
        </div>
    )

    // A click anywhere on a row flips its switch, except on the controls that act on their own
    // (the switch itself, the expand chevron, the drag grip).
    const rowClick = (e: MouseEvent) =>
        !(e.target as HTMLElement).closest("input, a, .form-check, .board-settings-column-grip")

    // Grid: the card always shows the job id, status and customer; everything else is switched here.
    const gridSettings = (
        <div className="board-settings-section">
            <div className="board-settings-section-head">
                <div>
                    <div className="board-settings-section-title">
                        <Icon icon="grid-2" type="solid" size={13} className="me-2" />
                        Card Details
                    </div>
                    <div className="board-settings-section-desc">
                        A card always shows the job id with its status and the customer. Choose what else it shows.
                    </div>
                </div>
                <a href="#" className="board-settings-reset" title="Back to the default card" onClick={e => {
                    e.preventDefault()
                    saveBoardSettings({ ...settings, grid: { ...settings.grid, fields: {} } })
                }}>
                    <Icon icon="rotate-left" size={11} className="me-1" />
                    Reset
                </a>
            </div>
            <div className="board-settings-columns">
                {
                    GRID_FIELDS.map(f => {
                        const on = gridFieldOn(settings, f.id)
                        const setOn = (v: boolean) => saveBoardSettings({ ...settings, grid: { ...settings.grid, fields: { ...settings.grid.fields, [f.id]: v } } })
                        return (
                            <div key={`grid-field-${f.id}`} className={`board-settings-column is-static is-clickable${on ? "" : " is-off"}`}
                                onClick={e => rowClick(e) && setOn(!on)}>
                                <span className="board-settings-column-icon"><Icon icon={f.icon} size={12} /></span>
                                <span className="board-settings-column-text">
                                    <span className="board-settings-column-title">{f.title}</span>
                                    <span className="board-settings-column-desc">{f.description}</span>
                                </span>
                                <ToggleSwitch raw small checked={on} onChange={setOn} />
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )

    const listSettings = (
        <div className="board-settings-section">
            <div className="board-settings-section-head">
                <div>
                    <div className="board-settings-section-title">
                        <Icon icon="table-columns" type="solid" size={13} className="me-2" />
                        Columns
                    </div>
                    <div className="board-settings-section-desc">
                        Choose what the list shows, drag the columns into the order you want, and open a column to pick what it shows.
                    </div>
                </div>
                <a href="#" className="board-settings-reset" title="Back to the default columns" onClick={e => {
                    e.preventDefault()
                    saveColumns(defaultBoardSettings().list.columns)
                }}>
                    <Icon icon="rotate-left" size={11} className="me-1" />
                    Reset
                </a>
            </div>

            <div className="board-settings-columns" onDragLeave={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) dragOverSet(undefined)
            }}>
                {/* Always first: it carries the selection box and the status. */}
                <div className="board-settings-column is-fixed">
                    <span className="board-settings-column-grip"><Icon icon="lock" size={11} /></span>
                    <span className="board-settings-column-icon"><Icon icon="hashtag" size={12} /></span>
                    <span className="board-settings-column-text">
                        <span className="board-settings-column-title">{LIST_ID_COLUMN.title}</span>
                        <span className="board-settings-column-desc">Always first</span>
                    </span>
                </div>

                {
                    columns.map((c, i) => {
                        const def = LIST_COLUMNS.find(d => d.id == c.id)!
                        return (
                            <div key={`board-col-${c.id}`}
                                className={`board-settings-column-wrap${dragFrom == i ? " is-dragging" : ""}${dragOver == i && dragFrom != undefined && dragFrom != i && dragFrom != i - 1 ? " drop-before" : ""}`}
                                draggable
                                onDragStart={e => {
                                    dragFromSet(i)
                                    e.dataTransfer.effectAllowed = "move"
                                }}
                                onDragOver={e => {
                                    e.preventDefault()
                                    // Upper half drops before this column, lower half after it.
                                    const r = e.currentTarget.getBoundingClientRect()
                                    dragOverSet(e.clientY < r.top + r.height / 2 ? i : i + 1)
                                }}
                                onDrop={e => {
                                    e.preventDefault()
                                    if (dragFrom != undefined && dragOver != undefined) moveColumn(dragFrom, dragOver)
                                    dragFromSet(undefined)
                                    dragOverSet(undefined)
                                }}
                                onDragEnd={() => {
                                    dragFromSet(undefined)
                                    dragOverSet(undefined)
                                }}>
                            <div className={`board-settings-column is-clickable${c.visible ? "" : " is-off"}`}
                                onClick={e => {
                                    if (!rowClick(e) || (c.visible && shownCount <= 1)) return
                                    toggleColumn(i, !c.visible)
                                }}>
                                <span className="board-settings-column-grip" title="Drag to reorder">
                                    <Icon icon="grip-dots-vertical" size={12} />
                                </span>
                                <span className="board-settings-column-icon"><Icon icon={def.icon} size={12} /></span>
                                <span className="board-settings-column-text">
                                    <span className="board-settings-column-title">{def.title}</span>
                                    <span className="board-settings-column-desc">{def.description}</span>
                                </span>
                                {
                                    // Columns made of several pieces open a list of them.
                                    hasParts(def) &&
                                    <a href="#" className={`board-settings-column-expand${expanded.includes(c.id) ? " open" : ""}`}
                                        title={expanded.includes(c.id) ? "Hide what it shows" : "Choose what it shows"}
                                        onClick={e => {
                                            e.preventDefault()
                                            expandedSet(x => x.includes(c.id) ? x.filter(id => id != c.id) : [...x, c.id])
                                        }}>
                                        <Icon icon="chevron-down" size={11} />
                                    </a>
                                }
                                <ToggleSwitch raw small checked={c.visible}
                                    // At least one column besides the job id stays on.
                                    disabled={c.visible && shownCount <= 1}
                                    onChange={v => toggleColumn(i, v)} />
                            </div>
                            {
                                hasParts(def) && expanded.includes(c.id) &&
                                <div className={`board-settings-parts${c.visible ? "" : " is-off"}`}>
                                    {
                                        def.parts!.map(part => (
                                            <label key={`part-${c.id}-${part.id}`}
                                                className={`board-settings-part${part.locked ? " is-locked" : ""}`}
                                                title={part.locked ? "Always shown" : undefined}>
                                                <input type="checkbox" className="form-check-input"
                                                    checked={columnPartOn(settings, c.id, part.id)}
                                                    disabled={part.locked}
                                                    onChange={e => togglePart(i, part.id, e.target.checked)} />
                                                <span>{part.title}</span>
                                            </label>
                                        ))
                                    }
                                </div>
                            }
                            </div>
                        )
                    })
                }
                {
                    dragOver == columns.length && dragFrom != undefined && dragFrom != columns.length - 1 &&
                    <div className="board-settings-drop-end" />
                }
            </div>
        </div>
    )

    return (
        <Drawer id="drawer-jobs-board-settings" title="Customize Board" titleIcon="sliders"
            subtitle={view?.title} onClose={props.onClose}>

            {
                // Each view has settings of its own; the drawer shows the ones of the view the
                // person is in (only the list view has any so far).
                view?.id == "list" ? <>{rowTintSettings}{densitySettings}{listSettings}</> :
                view?.id == "grid" ? <>{rowTintSettings}{densitySettings}{gridSettings}</> :
                    <div className="board-settings-empty">
                        <Icon icon="sliders" size={22} className="mb-3" />
                        <div>Nothing to customize in this view yet.</div>
                    </div>
            }

        </Drawer>
    )
}
